import { FEATURE_REQUEST_MAX_IMAGE_COUNT } from '@lbc/shared';
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import { api } from '../../services/api/client';

const SUCCESS_MESSAGE = 'Dein Feature-Request ist eingegangen. Wir prüfen ihn.';

interface FeatureRequestFormProps {
  groupId: string;
  onAnnouncement: (message: string) => void;
}

export function FeatureRequestForm({ groupId, onAnnouncement }: FeatureRequestFormProps) {
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imageSelectionInvalid, setImageSelectionInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const requestId = useRef<string | undefined>(undefined);
  const submitting = useRef(false);

  function toggleOpen() {
    if (open) {
      setImages([]);
      setImageSelectionInvalid(false);
    }
    setOpen(!open);
    setError('');
  }

  function chooseImages(event: ChangeEvent<HTMLInputElement>) {
    setError('');
    setImageSelectionInvalid(false);
    const files = event.target.files;
    if (files === null || files.length === 0) {
      setImages([]);
      return;
    }
    const selected = Array.from(files);
    if (selected.length > FEATURE_REQUEST_MAX_IMAGE_COUNT) {
      event.target.value = '';
      setImages([]);
      setImageSelectionInvalid(true);
      setError('Bitte füge höchstens fünf Bilder hinzu.');
      return;
    }
    if (selected.some(isClearlyNotImage)) {
      event.target.value = '';
      setImages([]);
      setImageSelectionInvalid(true);
      setError('Bitte wähle ausschließlich Bilder im Format JPEG, PNG oder HEIC.');
      return;
    }
    setImages(selected);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    if (imageSelectionInvalid) {
      setError('Bitte korrigiere zuerst die Bildauswahl.');
      return;
    }
    if (problem.trim() === '') {
      setError('Bitte beschreibe kurz, welches Problem du lösen möchtest.');
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    const id = requestId.current ?? crypto.randomUUID();
    requestId.current = id;
    try {
      await api.sendFeatureRequest(groupId, {
        id,
        problem: problem.trim(),
        description: description.trim(),
        ...(images.length === 0 ? {} : { images }),
      });
      setSuccess(true);
      onAnnouncement(SUCCESS_MESSAGE);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Dein Feature-Request konnte gerade nicht gesendet werden. Bitte versuche es erneut.',
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  function startAnother() {
    setProblem('');
    setDescription('');
    setImages([]);
    setImageSelectionInvalid(false);
    setError('');
    setSuccess(false);
    requestId.current = undefined;
  }

  return (
    <div className="feature-request">
      <button
        className="button button--text feature-request__link"
        type="button"
        aria-expanded={open}
        aria-controls="feature-request-form"
        onClick={toggleOpen}
      >
        Hast du eine Idee für LBC?
      </button>
      {!open ? null : success ? (
        <div id="feature-request-form" className="feature-request__form" role="status">
          <p>{SUCCESS_MESSAGE}</p>
          <button className="button button--text" type="button" onClick={startAnother}>
            Weitere Idee senden
          </button>
        </div>
      ) : (
        <form id="feature-request-form" className="feature-request__form" onSubmit={submit}>
          <label>
            Welches Problem möchtest du lösen?
            <input
              value={problem}
              maxLength={200}
              required
              disabled={busy}
              onChange={(event) => setProblem(event.target.value)}
            />
          </label>
          <label>
            Beschreibe deine Idee näher
            <textarea
              value={description}
              maxLength={5000}
              rows={4}
              disabled={busy}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <label>
            Bilder hinzufügen <span className="optional">optional, max. 5</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic,.heic"
              multiple
              disabled={busy}
              onChange={chooseImages}
            />
          </label>
          {error === '' ? null : (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? 'Wird gesendet …' : 'Feature-Request senden'}
          </button>
        </form>
      )}
    </div>
  );
}

function isClearlyNotImage(file: File): boolean {
  const mimeType = file.type.trim().toLowerCase();
  return (
    mimeType !== '' &&
    mimeType !== 'application/octet-stream' &&
    mimeType !== 'application/heic' &&
    mimeType !== 'application/heif' &&
    !mimeType.startsWith('image/')
  );
}
